/**
 * Detail screen — single item with hero image + paragraphs.
 * Wave 2 / T6: sticky scroll header with title-reveal; sticky action bar under title.
 * Fundraisings get a dedicated layout (goal/progress/Support); other kinds use paragraphs.
 */

import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { getItem, getSection, type DetailItem, type SectionItem, ApiError } from "@/api";
import { useI18n } from "@/i18n";
import { hapticTap, openLink } from "@/tg/init";
import { DONATE_URL } from "@/lib/links";
import { SkeletonCard } from "@/components/Skeleton";
import ImageWithFallback from "@/components/ImageWithFallback";
import ProgressBar from "@/components/ProgressBar";
import ShareBlock from "@/components/ShareBlock";
import { getProgress, formatUAH } from "@/lib/getProgress";

/** Sticky bar rendered above content — shows Back always; reveals title on scroll. */
function StickyHeader({ title, scrolled }: { title: string; scrolled: boolean }) {
  const navigate = useNavigate();
  const { t } = useI18n();

  function handleBack() {
    hapticTap();
    navigate(-1);
  }

  return (
    <div
      style={{
        position: "sticky",
        top: 0,
        zIndex: 20,
        display: "flex",
        alignItems: "center",
        height: "48px",
        background: "var(--tg-bg)",
        borderBottom: scrolled ? "1px solid var(--surface-muted)" : "1px solid transparent",
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        boxShadow: scrolled ? "0 1px 8px rgba(0,0,0,0.08)" : "none",
      }}
    >
      {/* Back button */}
      <button
        onClick={handleBack}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          padding: "0 16px",
          height: "100%",
          color: "var(--brand-blue)",
          background: "none",
          border: "none",
          cursor: "pointer",
          fontSize: "14px",
          fontWeight: 500,
          flexShrink: 0,
        }}
      >
        <svg width="8" height="14" viewBox="0 0 8 14" fill="none" aria-hidden="true">
          <path d="M7 1L1 7L7 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {t("back")}
      </button>

      {/* Title — fades in when scrolled past hero */}
      <div
        style={{
          flex: 1,
          paddingRight: "56px", // balance the back button width
          overflow: "hidden",
          opacity: scrolled ? 1 : 0,
          transform: scrolled ? "translateY(0)" : "translateY(4px)",
          transition: "opacity 0.2s ease, transform 0.2s ease",
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: "15px",
            fontWeight: 600,
            color: "var(--tg-text)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            textAlign: "center",
          }}
        >
          {title}
        </p>
      </div>
    </div>
  );
}

/** Dedicated layout for fundraising detail — goal/progress/Support, NO hub boilerplate. */
function FundraisingDetail({
  item,
  fundraisingItem,
  heroSentinelRef,
  t,
  id,
}: {
  item: DetailItem;
  fundraisingItem: SectionItem | null;
  heroSentinelRef: React.RefObject<HTMLDivElement>;
  t: (key: import("@/i18n").TranslationKey) => string;
  id: string;
}) {
  const { percent, raised, target } = getProgress(id);
  // Prefer image from the list item (has image field); fall back to detail item image
  const heroImage = fundraisingItem?.image ?? item.image;
  // subtitle from the list item
  const subtitle = fundraisingItem?.subtitle ?? null;
  // "Open on site" link — the item's link (projects hub) or fallback
  const siteLink = item.link ?? "https://prytulafoundation.org/en/about/projects";

  function handleSupport() {
    hapticTap();
    openLink(DONATE_URL);
  }

  return (
    <>
      {/* Hero image — natural ratio, no crop */}
      <ImageWithFallback src={heroImage} alt={item.title} />

      {/* Sentinel — IntersectionObserver target at hero bottom edge */}
      <div ref={heroSentinelRef} style={{ height: 0, margin: 0 }} />

      <div className="px-4 pt-4">
        <h1
          style={{
            fontFamily: "var(--font-head, inherit)",
            fontSize: "22px",
            fontWeight: 700,
            lineHeight: 1.25,
            color: "var(--tg-text)",
            margin: "0 0 12px",
          }}
        >
          {item.title}
        </h1>
      </div>

      <div className="px-4 pt-4">
        {subtitle && (
          <p
            style={{
              fontSize: "15px",
              lineHeight: 1.6,
              color: "var(--tg-text)",
              marginBottom: "20px",
            }}
          >
            {subtitle}
          </p>
        )}

        {/* Goal label + amount */}
        <p
          style={{
            fontSize: "13px",
            fontWeight: 600,
            color: "var(--brand-blue, #4e51ff)",
            marginBottom: "6px",
          }}
        >
          {t("goal")}: {formatUAH(target)}
        </p>

        {/* Black progress bar */}
        <ProgressBar percent={percent} raised={raised} target={target} />

        {/* Demo data note */}
        <p
          style={{
            fontSize: "11px",
            color: "var(--muted, #aab2ca)",
            marginTop: "4px",
            marginBottom: "20px",
          }}
        >
          {t("demo_data_note")}
        </p>

        {/* Support button — primary blue pill, no arrow */}
        <button
          onClick={handleSupport}
          className="btn btn-primary"
          style={{ width: "100%", fontSize: "15px", padding: "14px 24px" }}
        >
          {t("support")}
        </button>

        <div className="pt-6 pb-2">
          <ShareBlock link={siteLink} title={item.title} label={t("share")} />
        </div>
      </div>
    </>
  );
}

export default function Detail() {
  const { kind = "", id = "" } = useParams<{ kind: string; id: string }>();
  const { lang, t } = useI18n();

  const [item, setItem] = useState<DetailItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imgFailed, setImgFailed] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // For fundraising kind: the matching SectionItem (has subtitle/image)
  const [fundraisingItem, setFundraisingItem] = useState<SectionItem | null>(null);

  // Sentinel ref placed at bottom of hero image
  const heroSentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = heroSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        // When sentinel goes out of view (hero scrolled past), reveal sticky title
        setScrolled(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: "0px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [item]); // re-run when item loads so sentinel is in DOM

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setImgFailed(false);
    setScrolled(false);
    setFundraisingItem(null);

    getItem(kind, id, lang)
      .then((data) => {
        if (!cancelled) {
          setItem(data);
          setLoading(false);
          hapticTap();
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : "Error loading");
          setLoading(false);
        }
      });

    // For fundraisings: also fetch the list to get subtitle/image
    if (kind === "fundraisings") {
      getSection("fundraisings", lang)
        .then((list) => {
          if (cancelled) return;
          const match = list.find((f) => f.id === id || f.id === id);
          setFundraisingItem(match ?? null);
        })
        .catch(() => {
          // graceful — fundraisingItem stays null
        });
    }

    return () => {
      cancelled = true;
    };
  }, [kind, id, lang]);

  return (
    <motion.div
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0, transition: { duration: 0.25 } }}
      exit={{ opacity: 0, x: -24 }}
      className="min-h-screen pb-8"
      style={{ background: "var(--tg-bg)" }}
    >
      <StickyHeader title={item?.title ?? ""} scrolled={scrolled} />

      {loading && (
        <div className="px-4">
          <SkeletonCard />
          <div className="space-y-2 mt-4">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="h-4 rounded animate-pulse"
                style={{ background: "var(--surface-muted)", width: i === 3 ? "60%" : "100%" }}
              />
            ))}
          </div>
        </div>
      )}

      {!loading && error && (
        <p className="text-center py-12 px-8" style={{ color: "var(--danger)" }}>
          {error}
        </p>
      )}

      {!loading && item && kind === "fundraisings" && (
        <FundraisingDetail
          item={item}
          fundraisingItem={fundraisingItem}
          heroSentinelRef={heroSentinelRef}
          t={t}
          id={id}
        />
      )}

      {!loading && item && kind !== "fundraisings" && (
        <>
          {/* Hero image — natural aspect ratio, no crop */}
          {item.image && !imgFailed ? (
            <img
              src={item.image}
              alt={item.title}
              loading="lazy"
              onError={() => setImgFailed(true)}
              style={{ display: "block", width: "100%", height: "auto" }}
            />
          ) : null}

          {/* Sentinel — IntersectionObserver target at hero bottom edge */}
          <div ref={heroSentinelRef} style={{ height: 0, margin: 0 }} />

          <div className="px-4 pt-4">
            {item.date && (
              <p className="text-xs mb-2" style={{ color: "var(--tg-hint)" }}>
                {new Date(item.date).toLocaleDateString(lang === "uk" ? "uk-UA" : "en-US", { day: "numeric", month: "long", year: "numeric" })}
              </p>
            )}

            <h1 className="text-xl font-bold leading-snug mb-3" style={{ color: "var(--tg-text)", fontFamily: "var(--font-head, inherit)" }}>
              {item.title}
            </h1>
          </div>

          <div className="px-4 pt-4">
            {/* Paragraphs — plain text only, no dangerouslySetInnerHTML */}
            <div className="space-y-4">
              {item.paragraphs.map((para, i) => (
                <p key={i} className="text-base leading-relaxed" style={{ color: "var(--tg-text)" }}>
                  {para}
                </p>
              ))}
            </div>
          </div>
          {item.link && (
            <div className="px-4 pt-6 pb-2">
              {item.kind === "news" ? (
                <ShareBlock link={item.link} title={item.title} />
              ) : (
                <ShareBlock link={item.link} title={item.title} label={t("share")} />
              )}
            </div>
          )}
        </>
      )}
    </motion.div>
  );
}
